import { Injectable, computed, signal } from '@angular/core';
import { TChatbotConversationHistory, TChatbotReceiveMessage, TChatbotTableRows } from '../types/chatbot-api.types';
import { MAX_RESPONSES } from '../types/chatbot.constants';
import { TChatAssistantMessage, TChatInitialQuestions, TChatMessage } from '../types/chatbot.types';
import { ChatbotLifecycleService } from './chatbot-lifecycle.service';
import { ChatbotWebSocketService } from './chatbot-websocket.service';

type TSendIntent = { type: 'user_message'; text: string } | { type: 'request_insights'; rows: TChatbotTableRows };

const BLANK_RESPONSE: Omit<TChatAssistantMessage, 'id' | 'author'> = {
  text: '',
  status: 'running',
  stages: [],
  table: null,
  chart: null,
  explanation: null,
  insights: [],
  insightsError: null,
  insightsStatus: 'idle',
  error: null,
};

@Injectable({ providedIn: 'root' })
export class ChatbotSessionService {
  readonly messages = signal<TChatMessage[]>([]);
  readonly draft = signal('');
  readonly initialQuestions = signal<TChatInitialQuestions | null>(null);
  readonly connectionStatus = this.chatbotWebSocket.status;

  readonly #conversationId = signal<string | null>(null);
  readonly #completedResponses = signal(0);
  readonly #hasRunStarted = signal(false);
  readonly #insightsTargetId = signal<string | null>(null);

  readonly #inFlightMessage = computed<TChatAssistantMessage | null>(
    () =>
      this.messages().find(
        (message): message is TChatAssistantMessage =>
          message.author === 'assistant' && (message.status === 'running' || message.status === 'cancelling'),
      ) ?? null,
  );

  readonly isConnected = computed(() => this.connectionStatus() === 'open');
  readonly isResponding = computed(() => !!this.#inFlightMessage());
  readonly isGeneratingInsights = computed(() => this.#insightsTargetId() !== null);
  readonly isBusy = computed(() => this.isResponding() || this.isGeneratingInsights());
  readonly isLimitReached = computed(() => this.#completedResponses() >= MAX_RESPONSES);
  readonly showsWelcome = computed(() => !this.messages().length && !!this.initialQuestions());
  readonly canSend = computed(
    () => !!this.draft().trim() && !this.isBusy() && !this.isLimitReached() && this.isConnected(),
  );
  readonly canCancel = computed(
    () => this.#inFlightMessage()?.status === 'running' && this.#hasRunStarted() && !!this.#conversationId(),
  );

  #messageCounter = 0;
  #lastIntent: TSendIntent | null = null;

  constructor(private chatbotWebSocket: ChatbotWebSocketService, private chatbotLifecycle: ChatbotLifecycleService) {
    this.chatbotWebSocket.messages$.subscribe((message) => this.#onMessage(message));

    this.chatbotWebSocket.status$.subscribe((status) => {
      if (status === 'open') this.#requestInitialQuestions();
      else this.#abandonRequest('Lost connection to Solvei8 AI.');
    });

    this.chatbotLifecycle.mode$.subscribe((mode) => {
      if (mode === 'disabled') this.reset();
      else if (mode === 'terminated') this.chatbotWebSocket.disconnect();
      else if (mode === 'active') this.chatbotWebSocket.connect();
    });
  }

  send(): void {
    if (!this.canSend()) return;

    const text = this.draft().trim();
    this.draft.set('');

    this.#appendUserMessage(text);
    this.#appendAssistantMessage();
    this.#dispatch({ type: 'user_message', text });
  }

  requestInsights(messageId: string): void {
    const message = this.#assistantMessage(messageId);
    if (this.isBusy() || !this.isConnected() || !message?.table) return;

    this.#insightsTargetId.set(messageId);
    this.#patchAssistant(messageId, { insights: [], insightsError: null, insightsStatus: 'running' });
    this.#dispatch({ type: 'request_insights', rows: message.table.rows });
  }

  retry(messageId: string): void {
    const message = this.#assistantMessage(messageId);
    const query = this.#previousUserText(messageId);
    if (this.isBusy() || !this.isConnected() || !message?.error?.retryable || !query) return;

    this.#patchAssistant(messageId, BLANK_RESPONSE);
    this.#dispatch({ type: 'user_message', text: query });
  }

  cancel(): void {
    const conversationId = this.#conversationId();
    const inFlight = this.#inFlightMessage();
    if (!this.canCancel() || !conversationId || !inFlight) return;

    this.chatbotWebSocket.send({ type: 'cancel', conversation_id: conversationId });
    this.#patchAssistant(inFlight.id, { status: 'cancelling' });
  }

  startNewChat(): void {
    this.#clearConversation();
    this.#requestInitialQuestions();
  }

  reset(): void {
    this.#clearConversation();
    this.chatbotWebSocket.disconnect();
  }

  #clearConversation(): void {
    this.messages.set([]);
    this.draft.set('');
    this.#conversationId.set(null);
    this.#completedResponses.set(0);
    this.#hasRunStarted.set(false);
    this.#insightsTargetId.set(null);
    this.#messageCounter = 0;
    this.#lastIntent = null;
  }

  #dispatch(intent: TSendIntent): void {
    this.#lastIntent = intent;
    const conversationId = this.#conversationId();

    const wasSent = this.chatbotWebSocket.send(
      conversationId
        ? { ...intent, conversation_id: conversationId }
        : { ...intent, conversation_history: this.#buildHistory() },
    );

    if (!wasSent) this.#abandonRequest('Could not reach Solvei8 AI. Check your connection and try again.');
  }

  #resendWithHistory(): void {
    if (!this.#lastIntent) return;

    this.#conversationId.set(null);
    this.#dispatch(this.#lastIntent);
  }

  #buildHistory(): TChatbotConversationHistory {
    const messages = this.messages();

    return messages.flatMap<TChatbotConversationHistory[number]>((message, index) => {
      if (message.author !== 'assistant' || message.status !== 'complete') return [];

      const question = messages[index - 1];
      const answer = { role: 'assistant' as const, content: message.text };

      return question?.author === 'user' ? [{ role: 'user' as const, content: question.text }, answer] : [answer];
    });
  }

  #onMessage(message: TChatbotReceiveMessage): void {
    switch (message.type) {
      case 'ready':
        this.#conversationId.set(message.conversation_id);
        break;

      case 'initial_questions':
        this.initialQuestions.set({ heading: message.heading, questions: message.questions });
        break;

      case 'run_started':
        this.#hasRunStarted.set(true);
        this.#patchRunning({
          stages: message.planned_stages.map((stage) => ({ ...stage, status: 'pending' as const })),
        });
        break;

      case 'stage':
        this.#patchRunning((running) => ({
          stages: running.stages.map((stage) =>
            stage.name === message.name ? { ...stage, status: message.status } : stage,
          ),
        }));
        break;

      case 'token':
        this.#patchRunning((running) => ({ text: running.text + message.text }));
        break;

      case 'result':
        this.#patchRunning({ table: { headers: message.headers, rows: message.rows } });
        break;

      case 'visualization':
        this.#patchRunning({ chart: message.highcharts });
        break;

      case 'explanation':
        this.#patchRunning({ explanation: message.text });
        break;

      case 'insights':
        this.#appendInsights(message.insights);
        break;

      case 'insights_finished':
        this.#settleInsights('complete');
        break;

      case 'error':
        this.#recordError(message.message, message.retryable);
        break;

      case 'run_cancelled':
        this.#settleRun('cancelled');
        break;

      case 'run_finished':
        this.#settleRun('complete');
        break;

      case 'unknown_conversation_id':
        this.#resendWithHistory();
        break;

      case 'session_terminated':
        this.chatbotLifecycle.terminate();
        break;

      case 'pong':
        break;

      default:
        break;
    }
  }

  #abandonRequest(reason: string): void {
    if (this.#inFlightMessage()?.status === 'cancelling') this.#settleRun('cancelled');
    else this.#recordError(reason, true);

    this.#settleInsights('complete');
  }

  #requestInitialQuestions(): void {
    if (this.messages().length) return;
    this.chatbotWebSocket.send({ type: 'request_initial_questions' });
  }

  #settleRun(status: TChatAssistantMessage['status']): void {
    const running = this.#inFlightMessage();
    if (!running) return;

    this.#hasRunStarted.set(false);
    this.#patchAssistant(running.id, { status });
    if (status === 'complete') this.#completedResponses.update((count) => count + 1);
  }

  #recordError(message: string, retryable: boolean): void {
    const insightsTargetId = this.#insightsTargetId();

    if (insightsTargetId) this.#patchAssistant(insightsTargetId, { insightsError: { message, retryable } });
    else this.#failRun(message, retryable);
  }

  #failRun(message: string, retryable: boolean): void {
    const running = this.#inFlightMessage();
    if (!running) return;

    this.#hasRunStarted.set(false);
    this.#patchAssistant(running.id, { status: 'failed', error: { message, retryable } });
  }

  #appendInsights(insights: TChatAssistantMessage['insights']): void {
    const targetId = this.#insightsTargetId();
    if (!targetId) return;

    const target = this.#assistantMessage(targetId);
    if (target) this.#patchAssistant(targetId, { insights: [...target.insights, ...insights] });
  }

  #settleInsights(insightsStatus: TChatAssistantMessage['insightsStatus']): void {
    const targetId = this.#insightsTargetId();
    if (!targetId) return;

    this.#insightsTargetId.set(null);
    this.#patchAssistant(targetId, { insightsStatus });
  }

  #assistantMessage(id: string): TChatAssistantMessage | null {
    return (
      this.messages().find(
        (message): message is TChatAssistantMessage => message.author === 'assistant' && message.id === id,
      ) ?? null
    );
  }

  #previousUserText(assistantId: string): string | null {
    const messages = this.messages();
    const index = messages.findIndex((message) => message.id === assistantId);
    const previous = index > 0 ? messages[index - 1] : null;

    return previous?.author === 'user' ? previous.text : null;
  }

  #appendUserMessage(text: string): void {
    this.#messageCounter += 1;
    this.messages.update((messages) => [...messages, { id: `${this.#messageCounter}`, author: 'user' as const, text }]);
  }

  #appendAssistantMessage(): void {
    this.#messageCounter += 1;
    this.messages.update((messages) => [
      ...messages,
      { id: `${this.#messageCounter}`, author: 'assistant' as const, ...BLANK_RESPONSE },
    ]);
  }

  #patchRunning(
    patch: Partial<TChatAssistantMessage> | ((running: TChatAssistantMessage) => Partial<TChatAssistantMessage>),
  ): void {
    const running = this.#inFlightMessage();
    if (running?.status !== 'running') return;

    this.#patchAssistant(running.id, typeof patch === 'function' ? patch(running) : patch);
  }

  #patchAssistant(id: string, patch: Partial<TChatAssistantMessage>): void {
    this.messages.update((messages) =>
      messages.map((message) =>
        message.author === 'assistant' && message.id === id ? { ...message, ...patch } : message,
      ),
    );
  }
}
